import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "@/components/site/Logo";
import { journals, site } from "@/content/site";

const nav = [
  { label: "Journals", to: "/#journals" },
  ...journals.map((j) => ({ label: j.abbr, to: `/index.php/${j.slug}` })),
  { label: "Submissions", to: "/index.php/ESGS/about/submissions" },
  { label: "Contact", to: "/index.php/ESGS/about/contact" },
];

const LangSwitch = () => (
  <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]" data-testid="language-switcher">
    <Globe className="w-4 h-4" aria-hidden="true" />
    {site.languages.map((l, i) => (
      <span key={l.code} className="flex items-center gap-2">
        {i > 0 && <span aria-hidden="true">/</span>}
        <a href={l.href} lang={l.code.replace("_", "-")} className={`link-u ${i === 0 ? "text-[var(--ink)] font-medium" : ""}`} data-testid={`lang-${l.code}`}>
          {l.label}
        </a>
      </span>
    ))}
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (to) => { setOpen(false); if (to.startsWith("/#")) { navigate("/"); setTimeout(() => document.getElementById(to.slice(2))?.scrollIntoView({ behavior: "smooth" }), 80); } };

  return (
    <header className={`sticky top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled ? "bg-[#FAF8F5]/90 backdrop-blur-md border-[var(--line)]" : "bg-[var(--paper)] border-transparent"}`} data-testid="site-header">
      <div className="hidden md:flex max-w-[1400px] mx-auto px-6 lg:px-12 h-9 items-center justify-between text-xs border-b border-[var(--line)]">
        <span className="font-mono-x tracking-[0.15em] uppercase text-[var(--ink-muted)]">Open access academic publishing</span>
        <div className="flex items-center gap-6">
          <LangSwitch />
          <Link to="/register" className="link-u text-[var(--ink)]" data-testid="header-register-link">Register</Link>
          <Link to="/login" className="link-u text-[var(--ink)]" data-testid="header-login-link">Login</Link>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between gap-6">
        <Logo />
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
          {nav.map((n) => n.to.startsWith("/#") ? (
            <button key={n.label} onClick={() => go(n.to)} className="text-[0.95rem] text-[var(--ink)] link-u" data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</button>
          ) : (
            <NavLink key={n.label} to={n.to} end className={({ isActive }) => `text-[0.95rem] link-u ${isActive ? "text-[var(--emerald)] font-semibold" : "text-[var(--ink)]"}`} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/search" aria-label="Search" className="w-11 h-11 grid place-items-center rounded-full hover:bg-[var(--paper-2)] transition-colors" data-testid="header-search-button">
            <Search className="w-5 h-5" />
          </Link>
          <Link to="/index.php/ESGS/about/submissions" className="hidden sm:inline-flex items-center h-11 px-5 rounded-full bg-[var(--emerald)] text-[var(--paper)] text-sm font-medium hover:bg-[var(--emerald-2)] transition-colors" data-testid="header-submit-button">
            Make a Submission
          </Link>
          <button className="lg:hidden w-11 h-11 grid place-items-center rounded-full hover:bg-[var(--paper-2)]" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} className="lg:hidden fixed inset-x-0 top-[72px] md:top-[108px] bottom-0 bg-[var(--paper)] border-t border-[var(--line)] overflow-y-auto" data-testid="mobile-menu">
            <nav aria-label="Mobile" className="px-6 py-8 flex flex-col">
              {nav.map((n, i) => (
                <motion.div key={n.label} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  {n.to.startsWith("/#") ? (
                    <button onClick={() => go(n.to)} className="w-full text-left font-display text-4xl py-3 border-b border-[var(--line)]" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</button>
                  ) : (
                    <Link to={n.to} onClick={() => setOpen(false)} className="block font-display text-4xl py-3 border-b border-[var(--line)]" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>{n.label}</Link>
                  )}
                </motion.div>
              ))}
              <div className="flex gap-6 mt-8 text-base">
                <Link to="/login" onClick={() => setOpen(false)} className="underline" data-testid="mobile-login-link">Login</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="underline" data-testid="mobile-register-link">Register</Link>
              </div>
              <div className="mt-6"><LangSwitch /></div>
              <Link to="/index.php/ESGS/about/submissions" onClick={() => setOpen(false)} className="mt-8 inline-flex justify-center items-center h-12 rounded-full bg-[var(--emerald)] text-[var(--paper)]" data-testid="mobile-submit-button">Make a Submission</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
